<script setup lang="ts">
import { truncate } from "fs/promises";
import { Close, Plus } from "@element-plus/icons-vue";
import { ElNotification } from "element-plus";
import { reactive, ref } from "vue";
import { ElementUIFormRef } from "~/types/element-ui/ElementUIFormRef";
import { FormRuleType } from "~/types/element-ui/FormRuleType";
import Helper from "~/composable/helper";
import { useOrganizationStore } from "~/projects/crm/stores/organizationStore";
import { useUsersStore } from "~/stores/usersStore";
import { useProductStore } from "~/stores/productStore";
import { Queue } from "~/projects/tele-sales/types/queue";
import { useTeleSalesStore } from "~/projects/tele-sales/stores/teleSalesStore";

const productsStore = useProductStore();
const teleSalesStore = useTeleSalesStore();

const formRef = ref<ElementUIFormRef | null>(null);

const formData = ref<Queue>({ ...teleSalesStore.selectedTeleSaleForEdit });
const emit = defineEmits(["close"]);
const rules = reactive<FormRuleType>({
  product_id: [
    {
      required: true,
      message: "product name is required",
      trigger: "blur",
    },
  ],
});
onBeforeMount(() => {
  if (teleSalesStore.selectedTeleSaleForEdit.sale_price === null) {
    formData.value.sale_price =
      teleSalesStore.selectedTeleSaleForEdit?.product?.price;
  }
});

const submitForm = async function () {
  if (!formRef.value) return;
  await formRef.value.validate(async (valid: boolean) => {
    if (!valid) return;
    if (formData.value.sale_status !== "success") {
      formData.value.sale_price = null;
      formData.value.discount = null;
      formData.value.discount_type = null;
    }
    teleSalesStore.setTeleSaleEditModalVisibility(false);

    const teleSaleQueueItem = await teleSalesStore.updateTeleSalesItem(
      formData.value,
    );

    await teleSalesStore.getTeleSalesCustomer(
      teleSalesStore.selectedTeleSalesCustomer.id,
    );
    await teleSalesStore.getTeleSalesCustomers();
    if (teleSaleQueueItem) {
      ElNotification({
        title: "Tele Sale Item updated",
        message: `Tele Sale Item updated`,
        type: "success",
      });
    }

    closeEditForm();
  });
};
const closeEditForm = function () {
  teleSalesStore.setTeleSaleEditModalVisibility(false);
  emit("close");
};
const props = defineProps({
  showEditModalHeader: {
    type: Boolean,
    default: true,
    required: false,
  },
  remoteSearch: {
    type: Boolean,
    default: false,
    required: false,
  },
});
// check if discount type is flat or percentage and discount is not null then calculate the sale_price from formdata.product?.price which changes in real time when discount changes or if dicount has no value then sale_price will be equal to product price computed
const changeSalePrice = function () {
  if (formData.value.discount_type === 0 && formData.value.discount) {
    formData.value.sale_price =
      formData.value?.product?.price - formData.value.discount;
  } else if (formData.value.discount_type === 1 && formData.value.discount) {
    formData.value.sale_price =
      formData.value?.product?.price -
      (formData.value?.product?.price * formData.value.discount) / 100;
  } else if (formData.value.sale_price == null) {
    formData.value.sale_price = formData.value?.product?.price;
  } else {
    formData.value.sale_price = formData.value?.product.sale_price;
  }
};
definePageMeta({
  layout: false,
});
</script>
<template>
  <EditModalHeader v-if="props.showEditModalHeader">
    <template #title>Edit Sale Item</template>
    <template #svg>
      <Svgs name="pen" />
    </template>
  </EditModalHeader>
  <el-form
    ref="formRef"
    :model="formData"
    :rules="rules"
    class="space-y-5"
    @submit.prevent="submitForm"
  >
    <div
      :class="` ${
        props.remoteSearch ? 'grid-cols-4-custom custom-gap' : 'space-y-5'
      } `"
    >
      <el-form-item class="form-elem-container" prop="product_id">
        <div class="form-elem-container">
          <div class="flex gap-2 items-center">
            <FormLabel :required="true"> Product </FormLabel>
          </div>
          <el-select
            v-model="formData.product_id"
            filterable
            :readonly="formData.product !== null"
            :disabled="formData.product !== null"
          >
            <el-option
              v-for="agent in productsStore.allProducts"
              :key="agent.id"
              :label="agent.name"
              :value="agent.id"
            />
          </el-select>
        </div>
      </el-form-item>
      <el-form-item
        v-if="formData.product"
        class="form-elem-container"
        prop="agent_id"
      >
        <div class="form-elem-container">
          <FormLabel>Product Price</FormLabel>
          <el-input
            v-model="formData.product.price"
            :readonly="true"
            :disabled="true"
          />
        </div>
      </el-form-item>
      <el-form-item
        v-if="formData.product"
        class="form-elem-container"
        prop="agent_id"
      >
        <div class="form-elem-container">
          <FormLabel>Sale Price</FormLabel>
          <el-input
            v-model="formData.sale_price"
            :readonly="true"
            :disabled="true"
          />
        </div>
      </el-form-item>
      <el-form-item
        v-if="formData.product"
        class="form-elem-container"
        prop="agent_id"
      >
        <div class="form-elem-container">
          <FormLabel>Discount Price</FormLabel>
          <div class="flex">
            <el-select
              v-model="formData.discount_type"
              :disabled="
                teleSalesStore.selectedTeleSaleForEdit.sale_status ==
                  'success' ||
                teleSalesStore.selectedTeleSaleForEdit.sale_status == 'failed'
              "
            >
              <el-option label="Flat" :value="0" />
              <el-option label="Percentage" :value="1" />
            </el-select>
            <el-input
              v-model="formData.discount"
              type="number"
              :readonly="
                teleSalesStore.selectedTeleSaleForEdit.sale_status ==
                  'success' ||
                teleSalesStore.selectedTeleSaleForEdit.sale_status == 'failed'
              "
              :disabled="
                teleSalesStore.selectedTeleSaleForEdit.sale_status ==
                  'success' ||
                teleSalesStore.selectedTeleSaleForEdit.sale_status == 'failed'
              "
              @change="changeSalePrice"
            />
          </div>
        </div>
      </el-form-item>
      <el-form-item class="form-elem-container" prop="sale_status">
        <div class="form-elem-container">
          <FormLabel>Sale Status</FormLabel>
          <el-select
            v-model="formData.sale_status"
            clearable
            filterable
            class="w-full"
          >
            <el-option label="Pending" value="pending" />
            <el-option label="Follow Up" value="follow_up" />
            <el-option label="Failed" value="failed" />
            <el-option label="Success" value="success" />
          </el-select>
        </div>
      </el-form-item>

      <el-form-item class="form-elem-container" prop="follow_up_date">
        <div class="form-elem-container">
          <FormLabel>Follow up date</FormLabel>
          <el-date-picker
            v-model="formData.follow_up_date"
            placeholder="Select date"
            type="date"
            format="YYYY-MM-DD"
            value-format="YYYY-MM-DD"
          />
        </div>
      </el-form-item>
      <!-- <el-form-item class="form-elem-container" prop="follow_up_media">
      <div class="form-elem-container">
        <FormLabel> Follow Up Media</FormLabel>
        <el-select
          v-model="formData.follow_up_media"
          clearable
          filterable
          @change="onChangeOrganization"
        >
          <el-option
            v-for="organization in getUniqueOrganizations(
              organizationStore.allOrganizations,
            )"
            :key="organization.id"
            :label="organization.name"
            :value="organization.id"
          ></el-option>
        </el-select>
      </div>
    </el-form-item> -->
      <el-form-item
        prop="remarks"
        :class="`${props.remoteSearch ? 'col-span-6-custom' : ''}`"
      >
        <div class="form-elem-container">
          <FormLabel>Remarks</FormLabel>
          <el-input
            v-model="formData.remarks"
            class="w-full h-full"
            type="textarea"
            rows=""
            resize="vertical"
          />
        </div>
      </el-form-item>
    </div>

    <div class="w-full flex gap-2 items-center justify-end">
      <NavButton type="button" @click="closeEditForm"> Cancel </NavButton>
      <NavButton class="bg-light-purple-nav" type="submit"> Update </NavButton>
    </div>
  </el-form>
</template>

<style scoped>
/* when small phone size detected grid cols will be 1 */
.custom-gap {
  display: grid;
  gap: 1rem !important;
}
@media (min-width: 640px) {
  .grid-cols-4-custom {
    grid-template-columns: repeat(6, 1fr);
  }
}
.col-span-6-custom {
  grid-column: span 6;
}

.datePickerClass {
  width: 100% !important;
}
</style>
